import { Accordion, Typography } from '@equinor/eds-core-react'
import AsyncResource from '../../components/async-resource/async-resource'
import { Breadcrumb } from '../../components/breadcrumb'
import { DocumentTitle } from '../../components/document-title'
import { ExternalLink } from '../../components/link/external-link'
import { RadixConfigFileLink } from '../../components/link/radix-config-file-link'
import { routes } from '../../router/routes'
import { pollingInterval } from '../../store/defaults'
import { type ApplicationRegistration, radixApi, useGetDeployKeyAndSecretQuery } from '../../store/radix-api'
import { withRouteParams } from '../../utils/router'
import { routeWithParams } from '../../utils/string'
import { BuildSecretsAccordion } from './components/BuildSecretsAccordion'
import ChangeAdminForm from './components/ChangeAdminForm'
import { ChangeConfigurationItemForm } from './components/ChangeCiForm'
import { ChangeConfigBranchForm } from './components/ChangeConfigBranchForm'
import { ChangeConfigFileForm } from './components/ChangeConfigFileForm'
import { ChangeRepositoryForm } from './components/ChangeRepositoryForm'
import DeleteApplicationForm from './components/DeleteApplicationForm'
import { ImageHubsAccordion } from './components/ImageHubsAccordion'
import { Overview } from './components/Overview'
import './style.css'
import { ConfigureDeployKey } from '../../components/configure-application-github/configure-deploy-key'
import { ConfigureGitHubWebhook } from '../../components/configure-application-github/configure-git-hub-webhook'
import { RegenerateDeployKeyScrim } from '../../components/configure-application-github/regenerate-deploy-key-scrim'
import { RegenerateSharedSecretScrim } from '../../components/configure-application-github/regenerate-shared-secret-scrim'

function getConfigBranch(configBranch?: string): string {
  return configBranch || 'master'
}

function getConfigBranchUrl({ configBranch, repository }: ApplicationRegistration): string {
  return `${repository}/tree/${getConfigBranch(configBranch)}`
}

function ConfigurationPage({ appName }: { appName: string }) {
  const { data: application, refetch, ...reqState } = radixApi.useGetApplicationQuery({ appName }, { pollingInterval })
  const registration = application?.registration

  const { data: secrets, refetch: refetchSecrets } = useGetDeployKeyAndSecretQuery(
    { appName: appName },
    { pollingInterval }
  )

  return (
    <main>
      <DocumentTitle title={`${appName} Configuration`} />
      <Breadcrumb
        links={[{ label: appName, to: routeWithParams(routes.app, { appName }) }, { label: 'Configuration' }]}
      />

      <AsyncResource asyncState={reqState}>
        {registration?.name && (
          <>
            <Overview adGroups={registration.adGroups ?? []} adUsers={registration.adUsers ?? []} appName={appName} />
            <section className="grid grid--gap-medium">
              <Typography variant="h4">GitHub</Typography>
              <Typography>
                Cloned from <ExternalLink href={registration.repository}>{registration.repository}</ExternalLink>
              </Typography>
              <Typography>
                Config branch{' '}
                <ExternalLink href={getConfigBranchUrl(registration)}>
                  {getConfigBranch(registration.configBranch)}
                </ExternalLink>
              </Typography>
              <Typography>
                Config file <RadixConfigFileLink registration={registration} />
              </Typography>
              <Typography>To integrate with GitHub you must add a deploy key and a webhook</Typography>

              <div className="grid grid--gap-small">
                <Accordion className="accordion" chevronPosition="right">
                  <Accordion.Item>
                    <Accordion.Header>
                      <Accordion.HeaderTitle>
                        <Typography>Add deploy key</Typography>
                      </Accordion.HeaderTitle>
                    </Accordion.Header>
                    <Accordion.Panel>
                      <ConfigureDeployKey deployKey={secrets} app={registration} />
                      <RegenerateDeployKeyScrim appName={registration.name} refetchSecrets={refetchSecrets} />
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion>

                <Accordion chevronPosition="right">
                  <Accordion.Item>
                    <Accordion.Header>
                      <Accordion.HeaderTitle>
                        <Typography>Add webhook</Typography>
                      </Accordion.HeaderTitle>
                    </Accordion.Header>
                    <Accordion.Panel>
                      <ConfigureGitHubWebhook
                        appName={registration.name}
                        repository={registration.repository}
                        sharedSecret={secrets?.sharedSecret}
                      />
                      <RegenerateSharedSecretScrim appName={registration.name} refetchSecrets={refetchSecrets} />
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion>
              </div>
            </section>

            <section className="grid grid--gap-small">
              <Typography variant="h4">App Secrets</Typography>
              <ImageHubsAccordion appName={appName} />
              <BuildSecretsAccordion appName={appName} />
            </section>

            <section className="grid grid--gap-small">
              <Typography variant="h4">Danger Zone</Typography>
              <ChangeAdminForm registration={registration} refetch={refetch} />
              <ChangeRepositoryForm appName={appName} repository={registration.repository} refetch={refetch} />
              <ChangeConfigBranchForm appName={appName} configBranch={registration.configBranch} refetch={refetch} />
              <ChangeConfigFileForm
                refetch={refetch}
                appName={appName}
                radixConfigFullName={registration.radixConfigFullName}
              />
              <ChangeConfigurationItemForm
                appName={appName}
                configurationItem={registration.configurationItem}
                refetch={refetch}
              />
              <DeleteApplicationForm appName={appName} />
            </section>
          </>
        )}
      </AsyncResource>
    </main>
  )
}
export default withRouteParams(ConfigurationPage)
